// **React Imports
import { memo, useMemo } from 'react'

// **MUI Imports
import { Box, Typography } from '@mui/material'

// **Component Imports
import CustomChip from '../chip'
import MarkdownRenderer from '../markdown'

// **Third party Imports
import DOMPurify from 'dompurify'

// **Utils Imports
import {
  QUESTION_CONTENT_TYPE_MAP,
  QUESTION_SUB_TYPE_LABEL
} from '../../utils/constant'

// **SCSS Imports
import './comprehension.scss'
import MCQTypeRenderer from './MCQTypeRenderer'

const ComprehensionQuestionRenderer = memo(
  ({
    question,
    questionNo,
    usedForEvaluation,
    readOnly = true,
    handleOptionSelected = () => {}
  }) => {
    const passageContent = useMemo(() => {
      return question?.content
        ? question?.contentType === QUESTION_CONTENT_TYPE_MAP.HTML
          ? DOMPurify.sanitize(question?.content)
          : question?.content
        : ''
    }, [question])

    return (
      <Box className="comprehension-renderer">
        {/* Header */}
        {readOnly ? (
          <Box className="comprehension-header">
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {`Question ${questionNo}`}
            </Typography>
          </Box>
        ) : (
          <></>
        )}

        {/* Split Panel */}
        <Box className="comprehension-split-panel">
          {/* Left - Passage */}
          <Box className="comprehension-passage">
            {question?.contentType === QUESTION_CONTENT_TYPE_MAP.MARKDOWN ? (
              <MarkdownRenderer value={passageContent} />
            ) : question?.contentType === QUESTION_CONTENT_TYPE_MAP.HTML ? (
              <Typography
                className="main-question html-markdown-table-and-list-styles"
                dangerouslySetInnerHTML={{ __html: passageContent }}
                sx={{ whiteSpace: 'pre-wrap' }}
              />
            ) : (
              <Typography
                className="main-question html-markdown-table-and-list-styles"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {passageContent}
              </Typography>
            )}
          </Box>

          {/* Right - Sub Questions */}
          <Box className="comprehension-sub-questions">
            {question?.subQuestions?.map((subQ, subIndex) => (
              <Box
                key={subQ?.id || subIndex}
                className="comprehension-sub-question-card"
              >
                {/* Sub Question Header */}
                <Box className="comprehension-sub-question-header">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {`Question ${questionNo}.${subIndex + 1}`}
                  </Typography>
                  <Box className="comprehension-sub-meta">
                    {subQ?.subtypeLabel && (
                      <CustomChip
                        size="small"
                        rounded
                        label={`${QUESTION_SUB_TYPE_LABEL?.[subQ?.subtype]}`}
                        color="info"
                      />
                    )}
                    {usedForEvaluation && subQ?.score != null ? (
                      <CustomChip
                        rounded
                        size="small"
                        label={`${subQ?.score} / ${subQ.mark} marks`}
                        color={
                          subQ?.score === 0
                            ? 'error'
                            : subQ?.score === subQ?.mark
                              ? 'success'
                              : 'warning'
                        }
                      />
                    ) : (
                      <CustomChip
                        size="small"
                        rounded
                        label={`${subQ?.mark || 0} Marks`}
                      />
                    )}
                  </Box>
                </Box>

                {/* Sub Question Content */}

                <Box className="comprehension-sub-question-body">
                  <MCQTypeRenderer
                    question={subQ}
                    questionNo={subIndex}
                    readOnly={readOnly}
                    usedForEvaluation={usedForEvaluation}
                    handleOptionSelected={handleOptionSelected}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    )
  }
)

export default ComprehensionQuestionRenderer
